// IMPORTANT NOTE: Please don't import other selectors from this file.
// Use secondOrder and *ThirdOrder for your selector if it has other
// dependencies.
import { createSelector } from 'reselect'
import { isEmpty } from 'lodash'

import { subtractMonths } from '../helpers/date'
import {
  dateCheckedExistsAndIsWithinLimit,
  plottrLicenseIsActive,
  proLicenseIsActive,
} from '../helpers/license'
import { fullSystemStateSelector } from './fullFileFirstOrder'

/**
 * @typedef {import('../index').PlottrState} PlottrState
 * @typedef {import('../index').ProLicense} ProLicense
 * @typedef {import('../index').PlottrLicense} PlottrLicense
 * @typedef {import('../index').TrialInfo} TrialInfo
 * @typedef {import('../types/system').SystemLicense} SystemLicense
 */

/**
 * @type {function(PlottrState): SystemLicense}
 */
export const licenseSelector = createSelector(fullSystemStateSelector, ({ license }) => {
  return (
    license ?? {
      trialInfo: null,
      plottrLicense: null,
      proLicense: null,
      licenseCheckInterval: null,
      couldNotContactLicenseServer: false,
      isProduction: true,
    }
  )
})
/**
 * @type {function(PlottrState): (TrialInfo | Null)}
 */
export const trialInfoSelector = createSelector(licenseSelector, ({ trialInfo }) => trialInfo)
/**
 * @type {function(PlottrState): number | Null}
 */
export const trialEndSelector = createSelector(trialInfoSelector, (trialInfo) => {
  if (trialInfo) {
    return trialInfo.endsAt
  } else {
    return null
  }
})
/**
 * @type {function(PlottrState): (number | Null)}
 */
export const daysLeftOfTrialSelector = createSelector(trialEndSelector, (endsAt) => {
  if (typeof endsAt === 'number') {
    let oneDay = 24 * 60 * 60 * 1000
    const today = new Date()
    return Math.round((endsAt - today.getTime()) / oneDay)
  } else {
    return null
  }
})
/**
 * @type {function(PlottrState): boolean}
 */
export const trialExpiredSelector = createSelector(daysLeftOfTrialSelector, (daysLeft) => {
  if (typeof daysLeft === 'number') {
    return daysLeft <= 0
  } else {
    return false
  }
})
/**
 * @type {function(PlottrState): boolean}
 */
export const canExtendSelector = createSelector(trialInfoSelector, (trialInfo) => {
  if (trialInfo && typeof trialInfo.extensions) {
    return trialInfo.extensions > 0
  } else {
    return false
  }
})
/**
 * @type {function(PlottrState): boolean}
 */
export const trialStartedSelector = createSelector(trialInfoSelector, (trialInfo) => {
  if (trialInfo) {
    const { startsAt, endsAt, extensions } = trialInfo
    return (
      startsAt !== null &&
      startsAt !== undefined &&
      endsAt !== null &&
      endsAt !== undefined &&
      extensions !== null &&
      extensions !== undefined
    )
  } else {
    return false
  }
})
/**
 * @type {function(PlottrState): (number | Null)}
 */
export const trialStartedOnSelector = createSelector(trialInfoSelector, (trialInfo) => {
  if (trialInfo) {
    return trialInfo.startsAt
  } else {
    return null
  }
})
/**
 * @type {function(PlottrState): (number | Null)}
 */
export const trialEndsOnSelector = createSelector(trialInfoSelector, (trialInfo) => {
  if (trialInfo) {
    return trialInfo.endsAt
  } else {
    return null
  }
})

/**
 * @type {function(PlottrState): (PlottrLicense | Null)}
 */
export const plottrLicenseSelector = createSelector(licenseSelector, ({ plottrLicense }) => {
  return plottrLicense || null
})

/**
 * @type {function(PlottrState): boolean}
 */
const isProductionSelector = createSelector(licenseSelector, ({ isProduction }) => {
  return isProduction
})

/**
 * @type {function(PlottrState): boolean}
 */
export const hasActivePlottrLicenseSelector = createSelector(
  plottrLicenseSelector,
  isProductionSelector,
  (license, isProduction) => {
    if (license) {
      const { dateChecked, expiresAt } = license
      return plottrLicenseIsActive(dateChecked, expiresAt, new Date(), isProduction)
    } else {
      return false
    }
  }
)

/**
 * @type {function(PlottrState, Date): Date}
 */
const dateNowSelector = (_state, dateNow) => {
  return dateNow
}

/**
 * @type {function(PlottrState, Date): boolean}
 */
export const needsToCheckPlottrLicense = createSelector(
  plottrLicenseSelector,
  dateNowSelector,
  isProductionSelector,
  (license, dateNow, isProduction) => {
    if (license) {
      const { dateChecked, expiresAt } = license
      if (expiresAt === null) {
        return false
      } else {
        return !dateCheckedExistsAndIsWithinLimit(dateChecked, dateNow, isProduction)
      }
    } else {
      return false
    }
  }
)
export const needsToCheckPlottrLicenseSelector = needsToCheckPlottrLicense
/**
 * @type {function(PlottrState): (ProLicense | Null)}
 */
export const proLicenseSelector = createSelector(licenseSelector, ({ proLicense }) => {
  return proLicense || null
})
/**
 * @type {function(PlottrState): boolean}
 */
export const hasActiveProLicenseSelector = createSelector(
  proLicenseSelector,
  isProductionSelector,
  (license, isProduction) => {
    if (license) {
      const { dateChecked, expiresAt } = license
      return proLicenseIsActive(dateChecked, expiresAt, new Date(), isProduction)
    } else {
      return false
    }
  }
)

/**
 * @type {function(PlottrState): boolean}
 */
export const hasAnActiveLicenseSelector = createSelector(
  hasActivePlottrLicenseSelector,
  hasActiveProLicenseSelector,
  (hasActivePlottrLicense, hasActiveProLicense) => {
    return hasActivePlottrLicense || hasActiveProLicense
  }
)
/**
 * @type {function(PlottrState, Date): boolean}
 */
export const hasNonExpiredPlottrLicenseSelector = createSelector(
  plottrLicenseSelector,
  dateNowSelector,
  isProductionSelector,
  (license, dateNow, isProduction) => {
    if (license) {
      const { dateChecked, expiresAt } = license
      if (expiresAt === null) {
        return true
      } else if (dateCheckedExistsAndIsWithinLimit(dateChecked, dateNow, isProduction)) {
        if (expiresAt && typeof expiresAt === 'string') {
          const dateExpiresAt = new Date(expiresAt)
          // @ts-ignore
          if (isNaN(dateExpiresAt)) {
            return false
          } else {
            const dateNowWithGracePeriod = subtractMonths(new Date(), 3)
            return dateNowWithGracePeriod < dateExpiresAt
          }
        } else {
          return expiresAt === null
        }
      } else {
        return false
      }
    } else {
      return false
    }
  }
)
/**
 * @type {function(PlottrState, Date): boolean}
 */
export const canReceiveUpdatesSelector = createSelector(
  hasActiveProLicenseSelector,
  hasNonExpiredPlottrLicenseSelector,
  (proHasNotExpired, plottrHasNotExpired) => {
    return proHasNotExpired || plottrHasNotExpired
  }
)
/**
 * @type {function(PlottrState): (Date | Null)}
 */
export const proLicenseExpirySelector = createSelector(proLicenseSelector, (license) => {
  if (license) {
    const { expiresAt } = license
    if (expiresAt && typeof expiresAt === 'string') {
      return new Date(expiresAt)
    } else {
      return null
    }
  } else {
    return null
  }
})
/**
 * @type {function(PlottrState, Date): boolean}
 */
export const needsToCheckProLicense = createSelector(
  proLicenseSelector,
  dateNowSelector,
  isProductionSelector,
  (license, dateNow, isProduction) => {
    if (license) {
      const { dateChecked } = license
      return !dateCheckedExistsAndIsWithinLimit(dateChecked, dateNow, isProduction)
    } else {
      return false
    }
  }
)
export const needsToCheckProLicenseSelector = needsToCheckProLicense

/**
 * @type {function(PlottrState): (ReturnType<typeof setTimeout> | Null)}
 */
export const licenseCheckIntervalSelector = createSelector(
  licenseSelector,
  ({ licenseCheckInterval }) => {
    return licenseCheckInterval ?? null
  }
)

/**
 * @type {function(PlottrState): boolean}
 */
export const failedToContactLicenseServerSelector = createSelector(
  licenseSelector,
  ({ couldNotContactLicenseServer }) => {
    return couldNotContactLicenseServer
  }
)

/**
 * @type {function(PlottrState): (Null | Date)}
 */
export const plottrLicenseExpirySelector = createSelector(plottrLicenseSelector, (license) => {
  if (license) {
    const { expiresAt } = license
    if (expiresAt && typeof expiresAt === 'string') {
      return new Date(expiresAt)
    } else {
      return null
    }
  } else {
    return null
  }
})
/**
 * @type {function(PlottrState): boolean}
 */
export const hasATrialExpiredOrNotSelector = createSelector(trialInfoSelector, (trialInfo) => {
  return !isEmpty(trialInfo)
})
/**
 * @type {function(PlottrState): boolean}
 */
export const hasAPlottrLicenseExpiredOrNotSelector = createSelector(
  plottrLicenseSelector,
  (plottrLicense) => {
    return !isEmpty(plottrLicense)
  }
)
/**
 * @type {function(PlottrState): boolean}
 */
export const hasAProLicenseExpiredOrNotSelector = createSelector(
  proLicenseSelector,
  (proLicense) => {
    return !isEmpty(proLicense)
  }
)

/**
 * @type {function(PlottrState): (Date | Null)}
 */
export const latestExpiryDateSelector = createSelector(
  proLicenseExpirySelector,
  plottrLicenseExpirySelector,
  hasAPlottrLicenseExpiredOrNotSelector,
  hasAProLicenseExpiredOrNotSelector,
  (
    proLicenseExpiry,
    plottrLicenseExpiry,
    hasAPlottrLicenseExpiredOrNot,
    hasAProLicenseExpiredOrNot
  ) => {
    // If we have a license without an expiry date, then it's
    // lifetime, so there's no expiry date.
    if (
      (hasAPlottrLicenseExpiredOrNot && !plottrLicenseExpiry) ||
      (hasAProLicenseExpiredOrNot && !proLicenseExpiry)
    ) {
      return null
    } else if (proLicenseExpiry && plottrLicenseExpiry) {
      // We have both expiry dates, which one comes later?
      if (proLicenseExpiry > plottrLicenseExpiry) {
        return proLicenseExpiry
      } else {
        return plottrLicenseExpiry
      }
    } else if (proLicenseExpiry) {
      // We only have a pro expiry date, and we have no Plottr
      // license.
      return proLicenseExpiry
    } else {
      // We don't have a pro license, so produce the Plottr expiry
      // whether or not it's there.
      return plottrLicenseExpiry
    }
  }
)
